import React, { useEffect, useRef } from 'react';
import * as am5 from '@amcharts/amcharts5';
import * as am5percent from '@amcharts/amcharts5/percent';
import am5themes_Animated from '@amcharts/amcharts5/themes/Animated';
import DynamicTheme from './Theme';
import { useTheme } from '../../context/ThemeContext';
import { normalizePieData } from './chartDataUtils';

const PieChart = ({ data, title, scale, titleFontSize, legendFontSize }) => {
    const chartRef = useRef(null);
    const { themeName } = useTheme();

    const scaleFactor = scale / 100;
    const resolvedTitleFontSize = titleFontSize ?? "1.5em";
    const resolvedLegendFontSize = legendFontSize ?? "0.8em";
    const resolvedValueFontSize = (0.8 * scaleFactor) + "em";

    useEffect(() => {
        if (data && chartRef.current) {
            const chartData = normalizePieData(data);
            const root = am5.Root.new(chartRef.current);

            root.setThemes([am5themes_Animated.new(root), DynamicTheme.new(root)]);
            root._renderer.resolution = 2;

            root.container.setAll({
                layout: root.verticalLayout,
                background: am5.Rectangle.new(root, {
                    fill: am5.color(0xffffff),
                    fillOpacity: 0
                })
            });

            if (title) {
                root.container.children.push(am5.Label.new(root, {
                    text: title,
                    fontSize: resolvedTitleFontSize,
                    fontWeight: "500",
                    textAlign: "center",
                    x: am5.p50,
                    centerX: am5.p50
                }));
            }

            const chart = root.container.children.push(
                am5percent.PieChart.new(root, {
                    layout: root.verticalLayout,
                    width: am5.percent(100),
                    height: am5.percent(100)
                })
            );

            const series = chart.series.push(
                am5percent.PieSeries.new(root, {
                    valueField: "value",
                    categoryField: "category",
                    alignLabels: true,
                    legendLabelText: "{category}",
                    legendValueText: "{valuePercentTotal.formatNumber('0.0')}%"
                })
            );

            series.labels.template.setAll({
                fontSize: resolvedValueFontSize,
                oversizedBehavior: "wrap",
                maxWidth: 140,
                text: "{category}: {valuePercentTotal.formatNumber('0.0')}%"
            });
            series.slices.template.setAll({
                tooltipText: "{category}: {value} ({valuePercentTotal.formatNumber('0.0')}%)"
            });

            series.data.setAll(chartData);

            const legend = chart.children.push(
                am5.Legend.new(root, {
                    centerX: am5.p50,
                    x: am5.p50,
                    marginTop: 12
                })
            );
            legend.labels.template.setAll({
                fontSize: resolvedLegendFontSize,
                fontWeight: "300"
            });
            legend.valueLabels.template.setAll({
                fontSize: resolvedLegendFontSize,
                fontWeight: "300"
            });
            legend.data.setAll(series.dataItems);

            series.appear(1000, 100);
            chart.appear(1000, 100);

            return () => {
                root.dispose();
            };
        }
    }, [data, title, scale, themeName, titleFontSize, legendFontSize]);

    return <div ref={chartRef} style={{ width: "100%", height: "100%" }} />;
};

export default PieChart;
